import { useState, useEffect } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  PermissionDenied,
  ajvResolver,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector } from '@store';
import RateRequestForm from '../../components/rate-request/form';
import { RateRequestFormValues } from './types';
import {
  useCreateRateRequestPairMutation,
  useLazyGetRateRequestQuery,
} from './store/services';

import ROUTES from '../../routes';

import cn from './rate-request.module.css';

const schema = {
  type: 'object',
  properties: {
    maxDeviationPercent: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Max. request deviation is required',
    },
    exchangeCurrencyFromId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency from is required',
    },
    exchangeCurrencyToId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Currency to is required',
    },
  },
  required: [
    'maxDeviationPercent',
    'exchangeCurrencyFromId',
    'exchangeCurrencyToId',
  ],
};

const RateRequestAddPair = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const {
    exchangeDictionaries: { activePermissions, currencies },
  } = useAppSelector((state) => state);

  const { currency } = useParams();

  const [create, { isLoading }] = useCreateRateRequestPairMutation();
  const [getList] = useLazyGetRateRequestQuery();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    setValue,
    watch,
    handleSubmit,
    setError,
  } = useForm<RateRequestFormValues>({
    resolver: ajvResolver(schema),
  });

  const currencyFrom = watch('exchangeCurrencyFromId');

  const navigate = useNavigate();
  const { search } = useLocation();

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});

    const url = generatePath(ROUTES.RATE_REQUEST_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    const body: RateRequestFormValues = {
      active: values.active,
      exchangeCurrencyFromId: values.exchangeCurrencyFromId,
      exchangeCurrencyToId: values.exchangeCurrencyToId,
      maxDeviationPercent: Number(values.maxDeviationPercent),
    };

    const response = (await create(body)) as {
      error: ResponseError | { data: { message: string } };
      data?: { data: RateRequestFormValues };
    };

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError,
        });
      } else {
        getList(`?take=20${search ? search.replace('?', '&') : ''}`);
        handleClose();
      }
    }
  });

  useEffect(() => {
    if (currency !== 'all') {
      setValue('exchangeCurrencyFromId', currency as string);
    }
  }, [currency]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <RateRequestForm
        control={control}
        errors={errors}
        currencyFrom={currencyFrom}
        currencies={currencies}
        isEditable={Boolean(
          activePermissions?.includes(
            PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_CREATE_ACTION,
          ),
        )}
      />
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="Add Pair"
        sidepanelExpandedKey="rate-request-panel"
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_CUSTOM_RATE_EXCHANGE_CREATE_ACTION,
            ) && (
              <ButtonPrimary
                text={isLoading ? 'Loading...' : 'Save'}
                onClick={onSubmit}
                disabled={!isDirty}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RateRequestAddPair;
